import type { LineComponent } from '@ims/core'
import type { Repository, RowOf } from '@ims/data'

// Recipes: what one unit of a product (a dish) uses of other products (its
// ingredients). The stock of the ingredients goes out with the sale.

export type ComponentRow = RowOf<'product_components'>

/**
 * A recipe's ingredients, one entry per ingredient (the same ingredient added
 * twice, e.g. on two devices offline, is added up).
 */
export function recipeOf(rows: readonly ComponentRow[]): LineComponent[] {
  const byIngredient = new Map<string, number>()
  for (const r of rows) {
    if (r.deleted_at) continue
    byIngredient.set(r.component_id, (byIngredient.get(r.component_id) ?? 0) + r.qty_milli)
  }
  return [...byIngredient].map(([productId, qtyMilli]) => ({ productId, qtyMilli }))
}

/**
 * The recipes of some products as they are right now, for a sale or a return:
 * only ingredients that still exist and are counted.
 */
export async function loadRecipes(
  repo: Repository,
  productIds: readonly (string | null | undefined)[],
): Promise<Map<string, LineComponent[]>> {
  const out = new Map<string, LineComponent[]>()
  const tracked = new Map<string, boolean>()
  for (const id of new Set(productIds.filter((p): p is string => !!p))) {
    const rows = (await repo.list('product_components', { where: { product_id: id }, limit: 500 }))
      .rows
    const recipe: LineComponent[] = []
    for (const c of recipeOf(rows)) {
      if (!tracked.has(c.productId)) {
        const p = await repo.get('products', c.productId)
        tracked.set(c.productId, !!p && !p.deleted_at && p.track_stock)
      }
      if (tracked.get(c.productId)) recipe.push(c)
    }
    if (recipe.length > 0) out.set(id, recipe)
  }
  return out
}

/**
 * What one unit of a dish costs to make: its ingredients at their unit cost.
 * Null when no ingredient has a known cost; `complete` is false when some do not.
 */
export function recipeCost(
  recipe: readonly LineComponent[],
  costs: ReadonlyMap<string, number>,
): { paisa: number; complete: boolean } | null {
  let paisa = 0
  let known = 0
  for (const c of recipe) {
    const cost = costs.get(c.productId)
    if (cost === undefined) continue
    known++
    paisa += (cost * c.qtyMilli) / 1000
  }
  return known === 0 ? null : { paisa: Math.round(paisa), complete: known === recipe.length }
}

/**
 * Unit costs with dishes added: a product with a recipe costs what its
 * ingredients cost (its own cost price is ignored), for the profit report.
 */
export function withRecipeCosts(
  costs: ReadonlyMap<string, number>,
  components: readonly ComponentRow[],
): Map<string, number> {
  const out = new Map(costs)
  const byProduct = new Map<string, ComponentRow[]>()
  for (const c of components) {
    if (c.deleted_at) continue
    const list = byProduct.get(c.product_id)
    if (list) list.push(c)
    else byProduct.set(c.product_id, [c])
  }
  for (const [productId, rows] of byProduct) {
    const cost = recipeCost(recipeOf(rows), costs)
    if (cost) out.set(productId, cost.paisa)
  }
  return out
}
